// ============================================================================
// STORE.JS - Tenant-Scoped Persistence Layer
// localStorage now, Firebase later
// ============================================================================

const STORAGE_KEYS = {
  TENANTS: 'rg_tenants',
  USERS: 'rg_users',
  PACKS: 'rg_packs',
  METRICS: 'rg_metrics',
  AUDIT_LOGS: 'rg_audit_logs',
  SETTINGS: 'rg_settings',
};

// ============================================================================
// HELPERS
// ============================================================================

const safeJsonParse = (str, fallback = null) => {
  try {
    return str ? JSON.parse(str) : fallback;
  } catch {
    return fallback;
  }
};

const getStorageItem = (key, fallback = null) => {
  if (typeof window === 'undefined') return fallback;
  return safeJsonParse(localStorage.getItem(key), fallback);
};

const setStorageItem = (key, value) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(value));
};

// ============================================================================
// DEFAULT DATA
// ============================================================================

const DEFAULT_TENANTS = {
  'demo-clinic': { id: 'demo-clinic', name: 'Demo Family Clinic', region: 'US-CA', seats: 10, status: 'active' },
  'neuro-partners': { id: 'neuro-partners', name: 'Neuro Partners Network', region: 'US-NY', seats: 25, status: 'active' },
};

const DEFAULT_USERS = [
  { id: 'user-1', email: 'sarah.chen@clinic.demo', name: 'Dr. Sarah Chen', role: 'gp_employee', tenantId: 'demo-clinic', specialtyIds: [], status: 'active' },
  { id: 'user-2', email: 'j.wilson@neuro.demo', name: 'Dr. James Wilson', role: 'specialist', tenantId: 'demo-clinic', specialtyIds: ['neurology'], status: 'active' },
  { id: 'user-3', email: 'admin@clinic.demo', name: 'Tenant Admin', role: 'tenant_admin', tenantId: 'demo-clinic', specialtyIds: [], status: 'active' },
  { id: 'user-4', email: 'platform@referralgate.io', name: 'Platform Admin', role: 'platform_admin', tenantId: null, specialtyIds: [], status: 'active' },
];

// Default neurology packs (built-in)
// NOTE: All packs MUST include metadata.specialtyId for analytics
// NOTE: notYetSteps can be strings (simple) or objects (structured with artifacts)
export const DEFAULT_PACKS = {
  headache: {
    metadata: { id: "headache", name: "Headache", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Brain", intent: "Classify headache type; decide imaging." },
    redFlags: [
      { id: "thunderclap", label: "Thunderclap headache (worst headache of life, sudden onset)", critical: true },
      { id: "focal_deficit", label: "New focal neurological deficit", critical: true },
      { id: "fever", label: "Fever with neck stiffness (meningismus)", critical: true }
    ],
    fields: [
      { id: "type", label: "Headache Type", type: "select", required: true, options: ["New onset", "Recurring", "Worsening chronic", "Stable chronic"] },
      { id: "severity", label: "Severity", type: "severity", required: true, min: 0, max: 10 },
      { id: "location", label: "Location", type: "multiselect", required: true, options: ["Unilateral", "Bilateral", "Frontal", "Temporal", "Occipital", "Retro-orbital"] },
      { id: "meds", label: "Current/Tried Medications", type: "text", required: true },
      { id: "exam", label: "Neurological Exam Findings", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "trial_treatment", label: "Trial first-line treatment", description: "Try OTC analgesics or triptans for 2-4 weeks", requiredArtifacts: ["Treatment log"], linkedFields: ["meds"] },
      { stepId: "document_response", label: "Document medication response", description: "Record which medications helped and which didn't", requiredArtifacts: ["Headache diary"], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted. Expect contact from the neurology office in 2-4 weeks.", Urgent: "Your symptoms require urgent evaluation. The neurology office will contact you within 24-48 hours.", NotYet: "Before we can complete your referral, your doctor needs to gather more information. Please follow up with them." },
    telehealth: { enabled: true }
  },
  seizure: {
    metadata: { id: "seizure", name: "First Seizure / LOC", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Zap", intent: "Differentiate seizure vs syncope; ensure safety." },
    redFlags: [
      { id: "status", label: "Status epilepticus (prolonged seizure >5 min)", critical: true },
      { id: "deficit", label: "Persistent neurological deficit post-event", critical: true }
    ],
    fields: [
      { id: "desc", label: "Event Description", type: "textarea", required: true },
      { id: "duration", label: "Duration", type: "select", required: true, options: ["<30 seconds", "30 sec - 2 min", "2-5 minutes", ">5 minutes"] },
      { id: "recovery", label: "Recovery Pattern", type: "select", required: true, options: ["Immediate", "Brief confusion (<15 min)", "Prolonged confusion (>15 min)"] },
      { id: "pmh", label: "Relevant History", type: "multiselect", required: true, options: ["None", "Prior seizures", "Prior stroke/TIA", "Head trauma", "Family history epilepsy"] },
      { id: "exam", label: "Post-Event Exam", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "labs", label: "Check metabolic labs", description: "BMP, glucose, calcium, magnesium", requiredArtifacts: ["Lab results"], linkedFields: [] },
      { stepId: "ecg", label: "Obtain ECG", description: "Rule out cardiac arrhythmia as cause", requiredArtifacts: ["ECG report"], linkedFields: [] },
      { stepId: "imaging", label: "Brain imaging", description: "CT head (acute) or MRI brain (outpatient)", requiredArtifacts: ["Imaging report"], linkedFields: [] },
      { stepId: "driving", label: "Driving counseling", description: "Document driving restriction discussion", requiredArtifacts: [], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted. IMPORTANT: Do not drive until cleared by neurology.", Urgent: "Urgent evaluation needed. Do not drive. The neurology office will contact you within 24 hours.", NotYet: "Additional testing is needed before your referral can be completed. Do not drive until cleared." },
    telehealth: { enabled: true }
  },
  neuropathy: {
    metadata: { id: "neuropathy", name: "Peripheral Neuropathy", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Hand", intent: "Identify treatable causes; decide EMG timing." },
    redFlags: [
      { id: "rapid", label: "Rapid ascending weakness (Guillain-Barré concern)", critical: true },
      { id: "respiratory", label: "Respiratory symptoms or dyspnea", critical: true }
    ],
    fields: [
      { id: "distribution", label: "Distribution Pattern", type: "select", required: true, options: ["Stocking-glove (symmetric)", "Feet only", "Hands only", "Asymmetric/patchy"] },
      { id: "symptoms", label: "Symptom Types", type: "multiselect", required: true, options: ["Numbness", "Tingling/paresthesias", "Burning pain", "Weakness", "Balance problems"] },
      { id: "severity", label: "Severity", type: "severity", required: true, min: 0, max: 10 },
      { id: "pmh", label: "Risk Factors", type: "multiselect", required: true, options: ["None", "Diabetes/prediabetes", "Alcohol use", "Chemotherapy", "B12 deficiency", "Thyroid disease"] },
      { id: "exam", label: "Sensory/Motor Exam", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "labs", label: "Check neuropathy labs", description: "HbA1c, B12, TSH, CBC, CMP", requiredArtifacts: ["Lab results"], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted. Nerve testing (EMG) may be scheduled.", Urgent: "Urgent evaluation needed for rapidly progressive symptoms.", NotYet: "Lab work is needed before your referral can be completed." },
    telehealth: { enabled: true }
  },
  movement: {
    metadata: { id: "movement", name: "Tremor / Parkinsonism", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Activity", intent: "Differentiate essential tremor vs parkinsonism." },
    redFlags: [
      { id: "acute", label: "Acute/sudden onset (stroke concern)", critical: true },
      { id: "cognitive", label: "Rapid cognitive decline with movement disorder", critical: true }
    ],
    fields: [
      { id: "symptom", label: "Primary Movement Problem", type: "select", required: true, options: ["Tremor at rest", "Tremor with action", "Slowness (bradykinesia)", "Stiffness (rigidity)", "Balance/gait problems"] },
      { id: "laterality", label: "Side Affected", type: "select", required: true, options: ["Symmetric (both sides)", "Right side dominant", "Left side dominant"] },
      { id: "meds", label: "Current Medications", type: "text", required: true },
      { id: "exam", label: "Gait & Movement Exam", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "med_review", label: "Medication reconciliation", description: "Review for drug-induced parkinsonism (metoclopramide, antipsychotics)", requiredArtifacts: ["Medication list"], linkedFields: ["meds"] },
      { stepId: "tsh", label: "Check thyroid function", description: "TSH to rule out thyroid tremor", requiredArtifacts: ["TSH result"], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted.", Urgent: "Urgent evaluation needed.", NotYet: "Please review medications with your doctor before referral." },
    telehealth: { enabled: true }
  },
  cognitive: {
    metadata: { id: "cognitive", name: "Cognitive Decline", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Sparkles", intent: "Rule out reversible causes of cognitive impairment." },
    redFlags: [
      { id: "rapid", label: "Rapid decline over weeks (not months)", critical: true },
      { id: "seizures", label: "New seizures with cognitive change", critical: true }
    ],
    fields: [
      { id: "onset", label: "Onset Pattern", type: "select", required: true, options: ["Gradual over years", "Gradual over months", "Rapid over weeks", "Stepwise (sudden drops)"] },
      { id: "domain", label: "Primary Domain Affected", type: "select", required: true, options: ["Memory (forgetting recent events)", "Language (word-finding)", "Executive (planning, judgment)", "Multiple domains"] },
      { id: "impact", label: "Functional Impact", type: "multiselect", required: true, options: ["No daily impact", "Difficulty with complex tasks (IADLs)", "Needs help with basic tasks (ADLs)", "Safety concerns"] },
      { id: "exam", label: "Cognitive/Neuro Exam", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "labs", label: "Check reversible causes", description: "CBC, CMP, TSH, B12, folate", requiredArtifacts: ["Lab results"], linkedFields: [] },
      { stepId: "depression", label: "Depression screening", description: "PHQ-9 or similar (pseudodementia)", requiredArtifacts: ["Depression screen result"], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted. Please bring a family member to your appointment.", Urgent: "Urgent evaluation needed for rapid cognitive change.", NotYet: "Testing is needed to rule out treatable causes of memory problems." },
    telehealth: { enabled: true }
  },
  demyelinating: {
    metadata: { id: "demyelinating", name: "Suspected MS", specialtyId: "neurology", specialty: "Neurology", version: "1.0.0", iconKey: "Eye", intent: "Evaluate for clinically isolated syndrome or MS." },
    redFlags: [
      { id: "vision", label: "Severe vision loss (optic neuritis)", critical: true },
      { id: "spinal", label: "Spinal cord syndrome (weakness + sensory level)", critical: true }
    ],
    fields: [
      { id: "symptom", label: "Primary Symptom", type: "select", required: true, options: ["Optic neuritis (vision loss, eye pain)", "Limb weakness", "Sensory symptoms (numbness, tingling)", "Ataxia/coordination problems", "Bladder dysfunction"] },
      { id: "onset", label: "Onset", type: "select", required: true, options: ["Sudden (hours)", "Subacute (days)", "Gradual (weeks)"] },
      { id: "prior", label: "Prior Neurological Episodes", type: "select", required: true, options: ["None - first episode", "1 prior episode", "2+ prior episodes", "Known MS diagnosis"] },
      { id: "exam", label: "Neurological Exam", type: "textarea", required: true }
    ],
    notYetSteps: [
      { stepId: "mri_brain", label: "MRI brain with contrast", description: "Evaluate for demyelinating lesions", requiredArtifacts: ["MRI brain report"], linkedFields: [] },
      { stepId: "mri_spine", label: "MRI spine if indicated", description: "If spinal cord symptoms present", requiredArtifacts: ["MRI spine report"], linkedFields: [] }
    ],
    patientMessages: { Routine: "Your referral has been submitted. MRI results will be reviewed.", Urgent: "Urgent evaluation needed. High-dose steroids may be considered.", NotYet: "MRI imaging is needed before your referral can be completed." },
    telehealth: { enabled: true }
  },
};

// ============================================================================
// TENANT SETTINGS STORE
// ============================================================================

/**
 * Get tenant settings
 */
export const getTenantSettings = (tenantId, settingId) => {
  const allSettings = getStorageItem(STORAGE_KEYS.SETTINGS, {});
  return allSettings[tenantId]?.[settingId] || null;
};

/**
 * Get security settings
 */
export const getSecuritySettings = (tenantId) => {
  return getTenantSettings(tenantId, 'security');
};

/**
 * Save tenant settings
 */
export const saveTenantSettings = (tenantId, settingId, settings) => {
  const allSettings = getStorageItem(STORAGE_KEYS.SETTINGS, {});
  if (!allSettings[tenantId]) allSettings[tenantId] = {};
  
  allSettings[tenantId][settingId] = {
    ...settings,
    updatedAt: new Date().toISOString(),
  };
  
  setStorageItem(STORAGE_KEYS.SETTINGS, allSettings);
  return { success: true, settingId };
};

/**
 * Save security settings
 */
export const saveSecuritySettings = (tenantId, settings) => {
  const validSettings = {
    allowPerCaseAuditRead: Boolean(settings.allowPerCaseAuditRead),
    allowedAuditSpecialtyIds: Array.isArray(settings.allowedAuditSpecialtyIds) 
      ? settings.allowedAuditSpecialtyIds 
      : [],
  };
  
  return saveTenantSettings(tenantId, 'security', validSettings);
};

// ============================================================================
// PACK STORE
// ============================================================================

/**
 * Get all packs for a tenant (merged: defaults + custom)
 */
export const getPacks = (tenantId) => {
  const allPackData = getStorageItem(STORAGE_KEYS.PACKS, {});
  const tenantPackData = allPackData[tenantId] || {};
  
  const merged = { ...DEFAULT_PACKS };
  
  Object.entries(tenantPackData).forEach(([packId, entry]) => {
    if (entry.versions && entry.activeVersion) {
      const activePack = entry.versions[entry.activeVersion];
      if (activePack) merged[packId] = activePack;
    }
  });
  
  return merged;
};

/**
 * Save a pack version (with history)
 */
export const savePackVersion = (tenantId, pack) => {
  const packId = pack.metadata.id;
  const version = pack.metadata.version;
  
  const allPackData = getStorageItem(STORAGE_KEYS.PACKS, {});
  if (!allPackData[tenantId]) allPackData[tenantId] = {};
  if (!allPackData[tenantId][packId]) {
    allPackData[tenantId][packId] = { versions: {}, activeVersion: null };
  }
  
  allPackData[tenantId][packId].versions[version] = { ...pack, savedAt: new Date().toISOString() };
  allPackData[tenantId][packId].activeVersion = version;
  
  setStorageItem(STORAGE_KEYS.PACKS, allPackData);
  return { success: true, version };
};

/**
 * Delete a custom pack
 */
export const deletePack = (tenantId, packId) => {
  const allPackData = getStorageItem(STORAGE_KEYS.PACKS, {});
  if (allPackData[tenantId]?.[packId]) {
    delete allPackData[tenantId][packId];
    setStorageItem(STORAGE_KEYS.PACKS, allPackData);
    return true;
  }
  return false;
};

/**
 * Get version history for a pack
 */
export const getPackVersions = (tenantId, packId) => {
  const allPackData = getStorageItem(STORAGE_KEYS.PACKS, {});
  const entry = allPackData[tenantId]?.[packId];
  if (!entry?.versions) return [];
  return Object.entries(entry.versions)
    .map(([v, data]) => ({ version: v, savedAt: data.savedAt, isActive: v === entry.activeVersion }))
    .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
};

/**
 * Check if pack is custom (tenant-uploaded)
 */
export const isCustomPack = (tenantId, packId) => {
  return !!getStorageItem(STORAGE_KEYS.PACKS, {})[tenantId]?.[packId];
};

// ============================================================================
// METRICS STORE
// ============================================================================

/**
 * Save a metric event (from createMetricEvent only!)
 */
export const saveMetricEvent = (tenantId, metricEvent) => {
  const eventId = `MTR-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
  
  const allMetrics = getStorageItem(STORAGE_KEYS.METRICS, {});
  if (!allMetrics[tenantId]) allMetrics[tenantId] = [];
  
  allMetrics[tenantId].push({ ...metricEvent, eventId, savedAt: new Date().toISOString() });
  
  // Keep last 1000 per tenant
  if (allMetrics[tenantId].length > 1000) {
    allMetrics[tenantId] = allMetrics[tenantId].slice(-1000);
  }
  
  setStorageItem(STORAGE_KEYS.METRICS, allMetrics);
  return { success: true, eventId };
};

/**
 * Get metrics for a tenant with pagination
 * @param {string} tenantId
 * @param {Object} options - Pagination options
 * @returns {{rows: Array, lastDoc: number|null, hasMore: boolean}}
 */
export const getMetrics = (tenantId, options = {}) => {
  const { pageSize = 50, startAfter = null, outcome = null, specialtyId = null } = options;
  
  const allMetrics = getStorageItem(STORAGE_KEYS.METRICS, {});
  let metrics = (allMetrics[tenantId] || [])
    .sort((a, b) => new Date(b.savedAt || b.timestamp) - new Date(a.savedAt || a.timestamp));
  
  // Apply filters
  if (outcome) {
    metrics = metrics.filter(m => m.outcome === outcome);
  }
  if (specialtyId) {
    metrics = metrics.filter(m => m.specialtyId === specialtyId);
  }
  
  // Apply pagination (startAfter is an index for local store)
  const startIndex = startAfter ? startAfter + 1 : 0;
  const endIndex = startIndex + pageSize;
  const rows = metrics.slice(startIndex, endIndex);
  
  const hasMore = endIndex < metrics.length;
  const lastDoc = rows.length > 0 ? startIndex + rows.length - 1 : null;
  
  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics for a specific specialty with pagination
 */
export const getMetricsBySpecialty = (tenantId, specialtyId, options = {}) => {
  const { pageSize = 50, startAfter = null, outcome = null } = options;
  
  const allMetrics = getStorageItem(STORAGE_KEYS.METRICS, {});
  let metrics = (allMetrics[tenantId] || [])
    .filter(m => m.specialtyId === specialtyId)
    .sort((a, b) => new Date(b.savedAt || b.timestamp) - new Date(a.savedAt || a.timestamp));
  
  if (outcome) {
    metrics = metrics.filter(m => m.outcome === outcome);
  }
  
  const startIndex = startAfter ? startAfter + 1 : 0;
  const endIndex = startIndex + pageSize;
  const rows = metrics.slice(startIndex, endIndex);
  
  const hasMore = endIndex < metrics.length;
  const lastDoc = rows.length > 0 ? startIndex + rows.length - 1 : null;
  
  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics for a specific user with pagination
 */
export const getMetricsByUser = (tenantId, userId, options = {}) => {
  const { pageSize = 50, startAfter = null, outcome = null } = options;
  
  const allMetrics = getStorageItem(STORAGE_KEYS.METRICS, {});
  let metrics = (allMetrics[tenantId] || [])
    .filter(m => m.submittedById === userId)
    .sort((a, b) => new Date(b.savedAt || b.timestamp) - new Date(a.savedAt || a.timestamp));
  
  if (outcome) {
    metrics = metrics.filter(m => m.outcome === outcome);
  }
  
  const startIndex = startAfter ? startAfter + 1 : 0;
  const endIndex = startIndex + pageSize;
  const rows = metrics.slice(startIndex, endIndex);
  
  const hasMore = endIndex < metrics.length;
  const lastDoc = rows.length > 0 ? startIndex + rows.length - 1 : null;
  
  return { rows, lastDoc, hasMore };
};

// ============================================================================
// AUDIT LOGS STORE
// ============================================================================

/**
 * Save an audit log entry
 */
export const saveAuditLog = (tenantId, auditLog) => {
  const runId = auditLog.runId;
  
  if (!runId) {
    throw new Error('saveAuditLog: auditLog.runId is required');
  }
  
  const allLogs = getStorageItem(STORAGE_KEYS.AUDIT_LOGS, {});
  if (!allLogs[tenantId]) allLogs[tenantId] = [];
  
  allLogs[tenantId].push({ ...auditLog, savedAt: new Date().toISOString() });
  
  // Keep last 500 per tenant
  if (allLogs[tenantId].length > 500) {
    allLogs[tenantId] = allLogs[tenantId].slice(-500);
  }
  
  setStorageItem(STORAGE_KEYS.AUDIT_LOGS, allLogs);
  return { success: true, runId };
};

/**
 * Get all audit logs for a tenant
 */
export const getAuditLogs = (tenantId) => {
  const allLogs = getStorageItem(STORAGE_KEYS.AUDIT_LOGS, {});
  return (allLogs[tenantId] || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

/**
 * Get audit logs for a specific specialty
 */
export const getAuditLogsBySpecialty = (tenantId, specialtyId) => {
  const allLogs = getAuditLogs(tenantId);
  return allLogs.filter(log => log.specialtyId === specialtyId);
};

/**
 * Get audit logs for a specific user
 */
export const getAuditLogsByUser = (tenantId, userId) => {
  const allLogs = getAuditLogs(tenantId);
  return allLogs.filter(log => log.context?.userId === userId);
};

/**
 * Get a single audit log by runId
 */
export const getAuditLog = (tenantId, runId) => {
  const allLogs = getAuditLogs(tenantId);
  return allLogs.find(log => log.runId === runId) || null;
};

// ============================================================================
// TENANT STORE
// ============================================================================

export const getTenants = () => getStorageItem(STORAGE_KEYS.TENANTS, DEFAULT_TENANTS);
export const getTenant = (tenantId) => getTenants()[tenantId] || null;

export const saveTenant = (tenant) => {
  const tenants = getTenants();
  tenants[tenant.id] = tenant;
  setStorageItem(STORAGE_KEYS.TENANTS, tenants);
};

export const deleteTenant = (tenantId) => {
  const tenants = getTenants();
  delete tenants[tenantId];
  setStorageItem(STORAGE_KEYS.TENANTS, tenants);
  
  // Cleanup packs and metrics
  const packs = getStorageItem(STORAGE_KEYS.PACKS, {});
  delete packs[tenantId];
  setStorageItem(STORAGE_KEYS.PACKS, packs);
  
  const metrics = getStorageItem(STORAGE_KEYS.METRICS, {});
  delete metrics[tenantId];
  setStorageItem(STORAGE_KEYS.METRICS, metrics);
};

// ============================================================================
// USER STORE
// ============================================================================

export const getUsers = () => getStorageItem(STORAGE_KEYS.USERS, DEFAULT_USERS);
export const getTenantUsers = (tenantId) => getUsers().filter(u => u.tenantId === tenantId);

export const saveUser = (user) => {
  const users = getUsers();
  const idx = users.findIndex(u => u.id === user.id);
  if (idx >= 0) users[idx] = { ...users[idx], ...user };
  else users.push(user);
  setStorageItem(STORAGE_KEYS.USERS, users);
};

export const updateUserStatus = (userId, status) => {
  const users = getUsers();
  const idx = users.findIndex(u => u.id === userId);
  if (idx >= 0) {
    users[idx] = { ...users[idx], status };
    setStorageItem(STORAGE_KEYS.USERS, users);
    return { success: true };
  }
  return { success: false };
};

export const deleteUser = (userId) => {
  setStorageItem(STORAGE_KEYS.USERS, getUsers().filter(u => u.id !== userId));
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Packs
  getPacks,
  savePackVersion,
  deletePack,
  getPackVersions,
  isCustomPack,
  DEFAULT_PACKS,
  
  // Metrics
  saveMetricEvent,
  getMetrics,
  getMetricsBySpecialty,
  getMetricsByUser,
  
  // Audit Logs
  saveAuditLog,
  getAuditLogs,
  getAuditLogsBySpecialty,
  getAuditLogsByUser,
  getAuditLog,
  
  // Tenants
  getTenants,
  getTenant,
  saveTenant,
  deleteTenant,
  
  // Tenant Settings
  getTenantSettings,
  getSecuritySettings,
  saveTenantSettings,
  saveSecuritySettings,
  
  // Users
  getUsers,
  getTenantUsers,
  saveUser,
  updateUserStatus,
  deleteUser,
};

